import { createApi } from "@reduxjs/toolkit/query/react";

import { REQUEST_METHODS } from "@/constants/global-constants";
import { IActivity } from "@/interfaces/activity.interface";
import {
  TDrafts,
  TaskClaimsData,
  TclaimsApproved,
  Tinvoice,
} from "@/interfaces/claims.interface";
import { TAllOptions } from "@/schemas/GlobalSchema";
import {
  TClaimsOption,
  TEstimatesReviewSchema,
} from "@/schemas/project-register.schema";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

export const claimsApi = createApi({
  reducerPath: "claimsApi",
  baseQuery: BaseWithAuth(),
  tagTypes: [
    "claims",
    "single-claims",
    "options",
    "claimType",
    "drafts",
    "activity",
  ],
  endpoints: (builder) => ({
    // getClaims by WS
    getClaims: builder.query<
      GeneralResponse<TaskClaimsData[]>,
      { status: string; filter: string }
    >({
      query: ({ status, filter }) =>
        `workspace/${getWorkspace()}/claims?status=${status}${filter}`,
      providesTags: ["claims"],
    }),
    getSingleClaims: builder.query<
      GeneralSingleResponse<TaskClaimsData>,
      { claimsId: string }
    >({
      query: ({ claimsId }) => `workspace/${getWorkspace()}/claims/${claimsId}`,
      providesTags: ["single-claims"],
    }),

    getSingleActivity: builder.query<
      GeneralSingleResponse<IActivity[]>,
      { claimsId: string }
    >({
      query: ({ claimsId }) =>
        `workspace/${getWorkspace()}/claims/${claimsId}/activity`,
      providesTags: ["activity"],
    }),

    // CUD operation for claims
    mutateClaims: builder.mutation<
      TClaimsOption,
      {
        method: string;
        data: Partial<TClaimsOption> | object;

        claimsId: string;
      }
    >({
      query: ({ data, method, claimsId }) => ({
        url: `workspace/${getWorkspace()}/claims/${claimsId}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["claims", "single-claims", "activity"],
    }),

    // get claims detail
    getClaimsDetail: builder.query<
      TclaimsApproved[] | Tinvoice[],
      { claimsId: string; claimType: string }
    >({
      query: ({ claimsId, claimType }) =>
        `workspace/${getWorkspace()}/claims/${claimsId}/${claimType}`,
      providesTags: ["claimType"],
      transformResponse: (
        response: APIResponse<TclaimsApproved[] | Tinvoice[]>,
      ) => response?.data,
    }),

    getSingleApproved: builder.query<
      TclaimsApproved,
      { claimsId: string; claimDetailId: string }
    >({
      query: ({ claimsId, claimDetailId }) =>
        `workspace/${getWorkspace()}/claims/${claimsId}/approved/${claimDetailId}`,
      providesTags: ["claimType"],
      transformResponse: (response: APIResponse<TclaimsApproved>) =>
        response?.data,
    }),

    // CUD operation for claims mutate
    mutateClaimsDetails: builder.mutation<
      TClaimsOption,
      {
        method: string;
        data?: object;
        claimsId: string;
        claimsType: string;
        claimsDetailId?: string;
      }
    >({
      query: ({ data, method, claimsId, claimsType, claimsDetailId }) => ({
        url: `workspace/${getWorkspace()}/claims/${claimsId}/${claimsType}/${claimsDetailId || ""}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["single-claims", "claimType", "activity"],
    }),

    mutateClaimsStatus: builder.mutation<
      object,
      {
        data?: object | null;
        claimsId: string;
      }
    >({
      query: ({ data, claimsId }) => ({
        url: `workspace/${getWorkspace()}/claims/${claimsId}/status`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["claims", "activity", "single-claims"],
    }),

    // getClaimsOptions by WS
    getClaimsOptions: builder.query<TAllOptions[], { forType?: string }>({
      query: ({ forType }) =>
        `workspace/${getWorkspace()}/claims/options${forType ? `?for=${forType}` : ""}`,
      providesTags: ["options"],
      transformResponse: (response: APIResponse<TAllOptions[]>) =>
        response?.data,
    }),

    //addClaimOption updateClaimOption deleteClaimOption
    mutateClaimOption: builder.mutation<
      TClaimsOption,
      {
        method: string;
        data?: object;
        optionId?: string;
      }
    >({
      query: ({ data, method, optionId }) => ({
        url: `workspace/${getWorkspace()}/claims/options/${optionId || ""}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["options", "claimType", "activity"],
    }),

    // Drafts

    // get claims estimates
    getClaimsEstimates: builder.query<
      GeneralResponse<TDrafts[]>,
      { claimsId: string; estimateId?: string; filters?: string }
    >({
      query: ({ claimsId, estimateId, filters }) =>
        `workspace/${getWorkspace()}/claims/${claimsId}/estimate/${estimateId || ""}${filters || ""}`,
      providesTags: ["drafts"],
    }),

    getSingleClaimsEstimates: builder.query<
      GeneralSingleResponse<TDrafts>,
      { claimsId: string; estimateId?: string; filters?: string }
    >({
      query: ({ claimsId, estimateId, filters }) =>
        `workspace/${getWorkspace()}/claims/${claimsId}/estimate/${estimateId || ""}${filters || ""}`,
      providesTags: ["drafts"],
    }),

    //addClaimOption updateClaimOption deleteClaimOption
    mutateClaimsEstimate: builder.mutation<
      TClaimsOption,
      {
        method: string;
        data?: object;
        claimsId: string;
        estimateId?: string;
        isDraft?: boolean;
      }
    >({
      query: ({ data, method, claimsId, estimateId, isDraft }) => ({
        url: `workspace/${getWorkspace()}/claims/${claimsId}/estimate${estimateId ? `/${estimateId}` : ""}${isDraft ? "/draft" : ""}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["drafts", "claims", "single-claims", "activity"],
    }),
    updateEstimateReview: builder.mutation<
      TClaimsOption,
      {
        data: TEstimatesReviewSchema;
        claimsId: string;
        estimateId: string;
      }
    >({
      query: ({ data, claimsId, estimateId }) => ({
        url: `workspace/${getWorkspace()}/claims/${claimsId}/estimate/${estimateId}/review`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
      invalidatesTags: ["single-claims", "activity"],
    }),

    approveClaimsEstimate: builder.mutation<
      TClaimsOption,
      {
        claimsId: string;
        estimateId: string;
        isDraft: boolean;
      }
    >({
      query: ({ claimsId, estimateId, isDraft }) => ({
        url: `workspace/${getWorkspace()}/claims/${claimsId}/estimate/${estimateId}/${isDraft ? "draft" : "approve"}`,
        method: "PATCH",
      }),
      invalidatesTags: ["drafts", "claimType", "activity"],
    }),

    deleteClaimsOption: builder.mutation<
      TClaimsOption,
      {
        optionId: string;
      }
    >({
      query: ({ optionId }) => ({
        url: `workspace/${getWorkspace()}/claims/options/${optionId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["claims", "claimType", "drafts", "activity"],
    }),

    updateTaskBreakDown: builder.mutation<
      TClaimsOption,
      {
        claimsId: string;
        claimsDetailId: string;
        data: object;
      }
    >({
      query: ({ claimsId, claimsDetailId, data }) => ({
        url: `workspace/${getWorkspace()}/claims/${claimsId}/approved/${claimsDetailId}/breakdown`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["claimType", "claims", "activity"],
    }),
  }),
});

export const {
  useGetClaimsQuery,
  useGetSingleClaimsQuery,
  useMutateClaimsMutation,
  useGetClaimsDetailQuery,
  useGetSingleApprovedQuery,
  useMutateClaimsStatusMutation,
  useMutateClaimsDetailsMutation,
  useGetClaimsOptionsQuery,
  useMutateClaimOptionMutation,

  // Drafts
  useGetClaimsEstimatesQuery,
  useGetSingleClaimsEstimatesQuery,
  useMutateClaimsEstimateMutation,
  useApproveClaimsEstimateMutation,
  useDeleteClaimsOptionMutation,
  useUpdateEstimateReviewMutation,

  // breakdown
  useUpdateTaskBreakDownMutation,

  //activity
  useGetSingleActivityQuery,
} = claimsApi;
